// FeedbackPage — full feedback inbox (ticket 126). SuperAdminPage's
// FeedbackList panel stays a 10-row summary with a "View all" link to here;
// this page owns sort/filter/search/bulk-triage against GET/POST
// /api/feedback (see feedback.py + routers/feedback_router.py).
import React, { useEffect, useState } from 'react';
import { Shield, Loader2, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TableScroller } from '@/components/ui/table-scroller';
import LongTextCell from '@/components/LongTextCell';
import { useAuth } from '../AuthContext';
import { feedbackApi } from './feedbackApi';
import { formatDateTime } from '../kit/time';

const STATUS_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'seen', label: 'Seen' },
  { key: 'done', label: 'Done' },
];

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// Backend only accepts sort on these two fields (feedback.py's
// `_SORT_FIELDS`); Tenant/User column clicks below re-order the already
// loaded rows client-side instead of round-tripping the server.
const SERVER_SORT_FIELDS = new Set(['created_at', 'status']);

function toIsoOrUndefined(dateStr) {
  if (!dateStr) return undefined;
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function SortHeader({ label, active, dir, onClick, testid }) {
  return (
    <th className="text-left px-2 py-2">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 hover:text-slate-900"
        data-testid={testid}
      >
        {label}
        {active && (dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </button>
    </th>
  );
}

export default function FeedbackPage() {
  const { user } = useAuth();
  const isOwner = !!user?.is_super_admin; // platform-owner flag (ticket 54), not an email match

  const [status, setStatus] = useState('all');
  const [tenantId, setTenantId] = useState('');
  const [userId, setUserId] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [hasSentry, setHasSentry] = useState(false);
  const [sort, setSort] = useState('-created_at');
  const [localSort, setLocalSort] = useState(null); // { field: 'tenant_name'|'user_name', dir }

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, seen: 0, done: 0, all: 0 });
  const [tenantOptions, setTenantOptions] = useState(() => new Map());
  const [userOptions, setUserOptions] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Debounce the free-text search (ticket 126).
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const mergeOptions = (rows) => {
    setTenantOptions((cur) => {
      const next = new Map(cur);
      rows.forEach((r) => { if (r.tenant_id) next.set(r.tenant_id, r.tenant_name || r.tenant_id); });
      return next;
    });
    setUserOptions((cur) => {
      const next = new Map(cur);
      rows.forEach((r) => { if (r.user_id) next.set(r.user_id, r.user_name || r.user_email || r.user_id); });
      return next;
    });
  };

  const currentParams = (before) => ({
    status: status !== 'all' ? status : undefined,
    tenant_id: tenantId || undefined,
    user_id: userId || undefined,
    q: q || undefined,
    since: toIsoOrUndefined(since),
    until: toIsoOrUndefined(until),
    has_sentry: hasSentry ? true : undefined,
    sort,
    before: before || undefined,
    limit: PAGE_SIZE,
  });

  const loadPage = (before) => {
    if (!isOwner) return;
    if (before) setLoadingMore(true); else setLoading(true);
    setError(null);
    feedbackApi.list(currentParams(before))
      .then(({ data }) => {
        const newItems = data.items || [];
        setCounts(data.counts || { new: 0, seen: 0, done: 0, all: 0 });
        setItems((cur) => (before ? [...cur, ...newItems] : newItems));
        setHasMore(newItems.length === PAGE_SIZE);
        mergeOptions(newItems);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Could not load feedback'))
      .finally(() => { setLoading(false); setLoadingMore(false); });
  };

  useEffect(() => {
    setSelectedIds(new Set());
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, status, tenantId, userId, q, since, until, hasSentry, sort]);

  const toggleServerSort = (field) => {
    if (!SERVER_SORT_FIELDS.has(field)) return;
    setLocalSort(null);
    setSort((cur) => (cur === `-${field}` ? field : `-${field}`));
  };

  const toggleLocalSort = (field) => {
    setLocalSort((cur) => {
      if (cur?.field === field) return { field, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
      return { field, dir: 'asc' };
    });
  };

  const displayItems = React.useMemo(() => {
    if (!localSort) return items;
    const { field, dir } = localSort;
    const sorted = [...items].sort((a, b) => {
      const av = (a[field] || '').toString().toLowerCase();
      const bv = (b[field] || '').toString().toLowerCase();
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [items, localSort]);

  const toggleExpanded = (id) => setExpanded((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSelected = (id) => setSelectedIds((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSelectAll = () => setSelectedIds((cur) => {
    if (cur.size === displayItems.length) return new Set();
    return new Set(displayItems.map((it) => it.feedback_id));
  });

  const markSeen = () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    feedbackApi.bulkSetStatus([...selectedIds], 'seen')
      .then(() => { setSelectedIds(new Set()); loadPage(); })
      .catch((e) => setError(e.response?.data?.detail || 'Could not update feedback'))
      .finally(() => setBulkBusy(false));
  };

  if (!isOwner) {
    return (
      <div className="p-10 max-w-lg mx-auto text-center">
        <Shield size={40} className="mx-auto text-slate-300 mb-3" />
        <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Owner Admin Only</div>
        <div className="text-sm text-slate-500 mt-2">This console is restricted to the software owner account.</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="feedback-page">
      <h1 className="font-serif text-2xl font-bold mb-6" style={{ color: '#070B5F' }}>Feedback</h1>

      {/* Status chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setStatus(c.key)}
            className={status === c.key ? 'btn-gold' : 'btn-outline'}
            data-testid={`feedback-chip-${c.key}`}
          >
            {c.label} ({counts[c.key] ?? 0})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-[13px]">
        {/* Radix items cannot carry an empty value, so "all" stands in for the cleared filter. */}
        <Select value={tenantId || 'all'} onValueChange={(v) => setTenantId(v === 'all' ? '' : v)}>
          <SelectTrigger aria-label="Tenant" data-testid="feedback-filter-tenant" className="h-9 w-full sm:w-48 bg-white text-[13px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tenants</SelectItem>
            {[...tenantOptions.entries()].map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={userId || 'all'} onValueChange={(v) => setUserId(v === 'all' ? '' : v)}>
          <SelectTrigger aria-label="User" data-testid="feedback-filter-user" className="h-9 w-full sm:w-48 bg-white text-[13px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {[...userOptions.entries()].map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search message…"
          className="w-56"
          data-testid="feedback-search"
        />

        <label className="flex items-center gap-1.5 text-slate-500">
          Since
          <Input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="w-40"
            data-testid="feedback-since"
          />
        </label>
        <label className="flex items-center gap-1.5 text-slate-500">
          Until
          <Input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="w-40"
            data-testid="feedback-until"
          />
        </label>

        <label className="flex items-center gap-1.5 text-slate-500">
          <Checkbox
            checked={hasSentry}
            onCheckedChange={(v) => setHasSentry(!!v)}
            data-testid="feedback-has-sentry"
          />
          Has Sentry event
        </label>

        {selectedIds.size > 0 && (
          <button
            type="button"
            className="btn-outline ml-auto"
            disabled={bulkBusy}
            onClick={markSeen}
            data-testid="feedback-bulk-mark-seen"
          >
            {bulkBusy && <Loader2 size={14} className="animate-spin mr-1 inline" />}
            Mark seen ({selectedIds.size})
          </button>
        )}
      </div>

      {error && <div className="p-4 text-red-800" data-testid="feedback-error">{error}</div>}
      {!error && loading && items.length === 0 && (
        <div className="p-6 text-slate-500 flex items-center gap-2" data-testid="feedback-loading">
          <Loader2 size={16} className="animate-spin" /> Loading feedback…
        </div>
      )}
      {!error && !loading && displayItems.length === 0 && (
        <div className="p-6 text-slate-500 italic" data-testid="feedback-empty">No feedback yet.</div>
      )}

      {!error && displayItems.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden text-[13px]">
          <TableScroller><table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-100">
                <th className="px-5 py-2 w-8">
                  <Checkbox
                    checked={selectedIds.size > 0 && selectedIds.size === displayItems.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="feedback-select-all"
                  />
                </th>
                <SortHeader
                  label="Created"
                  active={sort.replace('-', '') === 'created_at' && !localSort}
                  dir={sort === '-created_at' ? 'desc' : 'asc'}
                  onClick={() => toggleServerSort('created_at')}
                  testid="feedback-th-created"
                />
                <SortHeader
                  label="Tenant"
                  active={localSort?.field === 'tenant_name'}
                  dir={localSort?.field === 'tenant_name' ? localSort.dir : 'asc'}
                  onClick={() => toggleLocalSort('tenant_name')}
                  testid="feedback-th-tenant"
                />
                <SortHeader
                  label="User"
                  active={localSort?.field === 'user_name'}
                  dir={localSort?.field === 'user_name' ? localSort.dir : 'asc'}
                  onClick={() => toggleLocalSort('user_name')}
                  testid="feedback-th-user"
                />
                <th className="text-left px-2 py-2">Message</th>
                <SortHeader
                  label="Status"
                  active={sort.replace('-', '') === 'status' && !localSort}
                  dir={sort === '-status' ? 'desc' : 'asc'}
                  onClick={() => toggleServerSort('status')}
                  testid="feedback-th-status"
                />
              </tr>
            </thead>
            <tbody>
              {displayItems.map((it) => {
                const isExpanded = expanded.has(it.feedback_id);
                return (
                  <React.Fragment key={it.feedback_id}>
                    <tr
                      className="border-b border-slate-50 last:border-0 align-top cursor-pointer hover:bg-slate-50"
                      data-testid={`feedback-row-${it.feedback_id}`}
                      onClick={() => toggleExpanded(it.feedback_id)}
                    >
                      <td className="px-5 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(it.feedback_id)}
                          onCheckedChange={() => toggleSelected(it.feedback_id)}
                          data-testid={`feedback-select-${it.feedback_id}`}
                        />
                      </td>
                      <td className="px-2 py-2.5 text-slate-500 whitespace-nowrap">
                        {formatDateTime(it.created_at)}
                      </td>
                      <td className="px-2 py-2.5">
                        <LongTextCell id={`feedback-tenant-${it.feedback_id}`} text={it.tenant_name || it.tenant_id} lines={2} maxWidth={200} className="text-[#070B5F]" />
                      </td>
                      <td className="px-2 py-2.5">{it.user_name || it.user_email || it.user_id}</td>
                      <td className="px-2 py-2.5">
                        <LongTextCell id={`feedback-msg-${it.feedback_id}`} text={it.message} lines={2} maxWidth={384} minWidth="16rem" className="whitespace-pre-wrap" />
                      </td>
                      <td className="px-2 py-2.5">{it.status}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-slate-50 bg-slate-50/60" data-testid={`feedback-expanded-${it.feedback_id}`}>
                        <td />
                        <td colSpan={5} className="px-2 py-3 space-y-1.5">
                          <div className="whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid={`feedback-detail-message-${it.feedback_id}`}>{it.message}</div>
                          <div className="text-slate-500 [overflow-wrap:anywhere]" data-testid={`feedback-detail-page-${it.feedback_id}`}>Page: {it.page || '—'}</div>
                          <div className="text-slate-500 [overflow-wrap:anywhere]" data-testid={`feedback-detail-user-agent-${it.feedback_id}`}>User agent: {it.user_agent || '—'}</div>
                          <div className="text-slate-500 [overflow-wrap:anywhere]" data-testid={`feedback-detail-request-id-${it.feedback_id}`}>Request ID: {it.request_id || '—'}</div>
                          {it.sentry_event_id && (
                            <a
                              href={`https://sentry.io/organizations/${process.env.REACT_APP_SENTRY_ORG || ''}/issues/?query=${it.sentry_event_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                              data-testid={`feedback-detail-sentry-${it.feedback_id}`}
                            >
                              <ExternalLink size={12} /> Sentry event {it.sentry_event_id.slice(0, 8)}
                            </a>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table></TableScroller>
        </div>
      )}

      {!error && hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            className="btn-outline"
            disabled={loadingMore}
            onClick={() => loadPage(items[items.length - 1]?.created_at)}
            data-testid="feedback-load-more"
          >
            {loadingMore && <Loader2 size={14} className="animate-spin mr-1 inline" />}
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
